/**
 * Type-safe Event Bus for cross-store and cross-component communication.
 *
 * Usage:
 *
 * 1. Define your events in AppEvents type
 * 2. Emit events using eventBus.emit('eventName', payload)
 * 3. Listen to events using eventBus.on('eventName', callback)
 * 4. Clean up listeners using eventBus.off('eventName', callback)
 *
 * @example
 * ```typescript
 * // Emit an event
 * eventBus.emit('course:archived', { courseId: '123', courseName: 'Math' });
 *
 * // Listen to an event
 * eventBus.on('course:archived', (data) => {
 *   console.log(`Course ${data.courseName} was archived`);
 * });
 * ```
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Define all application events here with their payload types.
 * This ensures type safety across the entire application.
 */
export type AppEvents = {
  studentDocumentationChanged: { courseId: string };
  /**
   * Emitted when a course is archived/closed
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'course:archived': undefined; // no payload

  /**
   * Emitted when courses are reordered via drag and drop
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'course:reordered': undefined; // no payload

  /**
   * Emitted when a course's name or color is updated
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'course:updated': undefined; // no payload

  /**
   * Emitted when a new course is created
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'course:created': undefined; // no payload

  // Add more events here as needed
  // 'event:name': PayloadType;
};

type EventCallback<T> = (payload: T) => void;

class EventBus<Events extends Record<string, any>> {
  private events = new Map<keyof Events, EventCallback<any>[]>();

  /**
   * Subscribe to an event
   * @param event - The event name
   * @param callback - The callback function to execute when the event is emitted
   */
  on<K extends keyof Events>(
    event: K,
    callback: EventCallback<Events[K]>,
  ): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)?.push(callback);
  }

  /**
   * Unsubscribe from an event
   * @param event - The event name
   * @param callback - The callback function to remove
   */
  off<K extends keyof Events>(
    event: K,
    callback: EventCallback<Events[K]>,
  ): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emit an event with a payload
   * @param event - The event name
   * @param payload - The event payload
   */
  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      // Copy first so a callback that unsubscribes mid-emit can't corrupt iteration.
      [...callbacks].forEach((callback) => callback(payload));
    }
  }
}

/**
 * Global event bus instance.
 * Import this to emit or listen to events across your application.
 */
const eventBus = new EventBus<AppEvents>();

export const emitStudentDocumentationChanged = (courseId: string): void => {
  eventBus.emit('studentDocumentationChanged', { courseId });
};

export const onStudentDocumentationChanged = (
  callback: EventCallback<AppEvents['studentDocumentationChanged']>,
): (() => void) => {
  eventBus.on('studentDocumentationChanged', callback);
  return () => eventBus.off('studentDocumentationChanged', callback);
};

/**
 * Typed helper functions for common events.
 * These provide better IDE autocomplete and type safety.
 */

export const emitCourseArchived = () => {
  eventBus.emit('course:archived', undefined);
};

export const onCourseArchived = (
  callback: EventCallback<AppEvents['course:archived']>,
) => {
  eventBus.on('course:archived', callback);
  return () => eventBus.off('course:archived', callback);
};

export const emitCourseReordered = () => {
  eventBus.emit('course:reordered', undefined);
};

export const onCourseReordered = (
  callback: EventCallback<AppEvents['course:reordered']>,
) => {
  eventBus.on('course:reordered', callback);
  return () => eventBus.off('course:reordered', callback);
};

export const emitCourseUpdated = () => {
  eventBus.emit('course:updated', undefined);
};

export const onCourseUpdated = (
  callback: EventCallback<AppEvents['course:updated']>,
) => {
  eventBus.on('course:updated', callback);
  return () => eventBus.off('course:updated', callback);
};

export const emitCourseCreated = () => {
  eventBus.emit('course:created', undefined);
};

export const onCourseCreated = (
  callback: EventCallback<AppEvents['course:created']>,
) => {
  eventBus.on('course:created', callback);
  return () => eventBus.off('course:created', callback);
};
